import { useCallback } from 'react';
import { FJ131 } from '../../../services/j203Api';

/**
 * Calculation hook for J203 detail-level and master-level amounts.
 * BR-C01~C04: detail amount = unit price * quantity, master total = sum.
 */
export function useJ203Calculation() {
  const calcDetailRow = useCallback((row: Partial<FJ131>, currency: string, invoiceType: number): Partial<FJ131> => {
    const price = Number(row.fj131_16) || 0;
    const taxPrice = Number(row.fj131_161) || 0;
    const qty = Number(row.fj131_17) || 0;
    const isNTD = (currency || '').startsWith('NT');

    const preTaxAmt = isNTD ? Math.round(price * qty) : Math.round(price * qty * 100) / 100;
    const taxAmt = Math.round(taxPrice * qty * 100) / 100;

    return {
      ...row,
      fj131_23: preTaxAmt,
      fj131_231: taxAmt,
    };
  }, []);

  const recalcAll = useCallback((
    details: Partial<FJ131>[],
    currency: string,
    invoiceType: number,
  ): { details: Partial<FJ131>[]; totalPreTax: number; tax: number; totalWithTax: number } => {
    let totalPreTax = 0;
    let totalTax = 0;
    const isNTD = (currency || '').startsWith('NT');

    const updated = details.map(d => {
      const calc = calcDetailRow(d, currency, invoiceType);
      totalPreTax += calc.fj131_23 || 0;
      totalTax += calc.fj131_231 || 0;
      return calc;
    });

    if (isNTD) {
      totalPreTax = Math.round(totalPreTax);
    } else {
      totalPreTax = Math.round(totalPreTax * 100) / 100;
    }

    let tax = 0;
    if (invoiceType === 1 || invoiceType === 2) {
      tax = isNTD
        ? Math.round(totalPreTax * 0.05)
        : Math.round(totalPreTax * 0.05 * 100) / 100;
    }
    const totalWithTax = totalPreTax + tax;

    return { details: updated, totalPreTax, tax, totalWithTax };
  }, [calcDetailRow]);

  return { calcDetailRow, recalcAll };
}
